// src/utils/characterDraft.js
// Единый нормализатор черновика персонажа.
// Убирает рассинхрон между:
//  - background (объект {title, lineage, ...})
//  - background (строка)
//  - legend (объект из generate_background)
//  - description (то, что пишет редактор)
// Используется и в мастере создания, и в редакторе.

export const LEGEND_FIELDS = [
  'title',
  'lineage',
  'birthplace',
  'childhood_memory',
  'motivation',
  'background',
  'description',
];

export const emptyLegend = () => ({
  title: '',
  lineage: '',
  birthplace: '',
  childhood_memory: '',
  motivation: '',
  background: '',
  description: '',
});

/**
 * Приводит любой входящий legend/background к объекту известной формы.
 * Принимает:
 *  - строку  → кладёт в description и background
 *  - объект  → копирует известные ключи
 *  - объект с .legend { ... } → разворачивает
 *  - null / undefined / массив → пустая легенда
 */
export const normalizeLegend = (raw) => {
  const base = emptyLegend();
  if (!raw) return base;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return { ...base, description: trimmed, background: trimmed };
  }

  if (Array.isArray(raw)) {
    return base;
  }

  if (typeof raw === 'object') {
    const src =
      raw.legend && typeof raw.legend === 'object' ? raw.legend : raw;

    LEGEND_FIELDS.forEach((field) => {
      if (typeof src[field] === 'string' && src[field].trim()) {
        base[field] = src[field];
      }
    });

    // Синхронизация description <-> background
    if (!base.description) {
      base.description = base.background || base.childhood_memory || '';
    }
    if (!base.background) {
      base.background = base.description || '';
    }
  }

  return base;
};

/** Легенда пустая (все строки пустые)? */
export const isLegendEmpty = (legend) => {
  const norm = normalizeLegend(legend);
  return !LEGEND_FIELDS.some(
    (field) => typeof norm[field] === 'string' && norm[field].trim().length > 0
  );
};

/** Легенда → строка (для превью и для редактора). */
export const legendToString = (legend) => {
  const l = normalizeLegend(legend);
  const parts = [];
  if (l.title) parts.push(`Звание: ${l.title}`);
  if (l.lineage) parts.push(`Род: ${l.lineage}`);
  if (l.birthplace) parts.push(`Родина: ${l.birthplace}`);
  if (l.childhood_memory) parts.push(`Память детства: "${l.childhood_memory}"`);
  if (l.motivation) parts.push(`Мотивация: ${l.motivation}`);
  if (l.background) parts.push(`Предыстория: ${l.background}`);
  else if (l.description) parts.push(`Описание: ${l.description}`);
  return parts.join('\n\n');
};

/** Длина легенды в символах (для валидации "короткая ли"). */
export const legendLength = (legend) => {
  const l = normalizeLegend(legend);
  return LEGEND_FIELDS.reduce(
    (sum, field) => sum + (typeof l[field] === 'string' ? l[field].trim().length : 0),
    0
  );
};

/**
 * Нормализует персонажа из бэка в единый черновик.
 * Гарантирует:
 *  - background — объект известной формы
 *  - skills — массив
 *  - appearance — объект
 *  - race/template/temperament — как есть (могут быть id или объекты)
 */
export const normalizeCharacter = (character) => {
  if (!character || typeof character !== 'object') return null;

  return {
    ...character,
    background: normalizeLegend(
      character.background || character.legend || character.childhood_memory
    ),
    skills: Array.isArray(character.skills) ? character.skills : [],
    appearance:
      character.appearance && typeof character.appearance === 'object'
        ? character.appearance
        : {},
  };
};

/** Пустой черновик для мастера создания. */
export const createEmptyDraft = () => ({
  race: null,
  character_template: null,
  temperament: null,
  name: '',
  gender: 'neutral',
  background: emptyLegend(),
  skills: [],
  traits: [],
  preferences: [],
  phobias: [],
  appearance: {},
});
